import React from 'react';
import { MapPin, Landmark } from 'lucide-react';

const BillingAddressForm = ({
    billingData,
    onChange,
    errors = {},
    disabled = false,
    showSaveCheckbox = false,
    saveToProfile = false,
    onSaveToProfileChange = () => {},
    compact = false,
    showBankDetails = false
}) => {
    const handleChange = (e) => {
        const { name, value } = e.target;
        onChange({ ...billingData, [name]: value });
    };

    const inputClass = (fieldName) => `w-full px-3 py-2 border rounded-md transition-colors ${
        disabled
            ? 'bg-black/30 border-white/10 text-gray-400 cursor-not-allowed'
            : errors[fieldName]
                ? 'bg-black/30 border-red-500 text-white focus:outline-none focus:ring-2 focus:ring-red-500'
                : 'bg-black/30 border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent'
    }`;

    return (
        <div className={`bg-black/30 border border-white/10 rounded-xl ${compact ? 'p-4' : 'p-6'}`}>
            <div className="flex items-center mb-4">
                <MapPin className="w-5 h-5 text-blue-400 mr-2" />
                <h3 className="text-lg font-semibold text-white">Adresse de facturation</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nom ou Raison sociale */}
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Nom ou Raison sociale *
                    </label>
                    <input
                        type="text"
                        name="billingName"
                        value={billingData.billingName || ''}
                        onChange={handleChange}
                        disabled={disabled}
                        className={inputClass('billingName')}
                        placeholder="Nom complet ou nom de l'entreprise"
                    />
                    {errors.billingName && (
                        <p className="mt-1 text-sm text-red-400">{errors.billingName}</p>
                    )}
                </div>

                {/* Adresse ligne 1 */}
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Adresse *
                    </label>
                    <input
                        type="text"
                        name="billingAddressLine1"
                        value={billingData.billingAddressLine1 || ''}
                        onChange={handleChange}
                        disabled={disabled}
                        className={inputClass('billingAddressLine1')}
                        placeholder="Numéro et nom de rue"
                    />
                    {errors.billingAddressLine1 && (
                        <p className="mt-1 text-sm text-red-400">{errors.billingAddressLine1}</p>
                    )}
                </div>

                {/* Adresse ligne 2 */}
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Complément d'adresse
                    </label>
                    <input
                        type="text"
                        name="billingAddressLine2"
                        value={billingData.billingAddressLine2 || ''}
                        onChange={handleChange}
                        disabled={disabled}
                        className={inputClass('billingAddressLine2')}
                        placeholder="Bâtiment, étage, appartement... (optionnel)"
                    />
                </div>

                {/* Code postal */}
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Code postal *
                    </label>
                    <input
                        type="text"
                        name="billingPostalCode"
                        value={billingData.billingPostalCode || ''}
                        onChange={handleChange}
                        disabled={disabled}
                        className={inputClass('billingPostalCode')}
                        placeholder="75001"
                        maxLength={10}
                    />
                    {errors.billingPostalCode && (
                        <p className="mt-1 text-sm text-red-400">{errors.billingPostalCode}</p>
                    )}
                </div>

                {/* Ville */}
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Ville *
                    </label>
                    <input
                        type="text"
                        name="billingCity"
                        value={billingData.billingCity || ''}
                        onChange={handleChange}
                        disabled={disabled}
                        className={inputClass('billingCity')}
                        placeholder="Paris"
                    />
                    {errors.billingCity && (
                        <p className="mt-1 text-sm text-red-400">{errors.billingCity}</p>
                    )}
                </div>

                {/* Pays */}
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Pays *
                    </label>
                    <input
                        type="text"
                        name="billingCountry"
                        value={billingData.billingCountry || 'France'}
                        onChange={handleChange}
                        disabled={disabled}
                        className={inputClass('billingCountry')}
                        placeholder="France"
                    />
                    {errors.billingCountry && (
                        <p className="mt-1 text-sm text-red-400">{errors.billingCountry}</p>
                    )}
                </div>

                {/* SIRET / SIREN (optionnel) */}
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        SIRET ou SIREN *
                    </label>
                    <input
                        type="text"
                        name="siret"
                        value={billingData.siret || ''}
                        onChange={handleChange}
                        disabled={disabled}
                        className={inputClass('siret')}
                        placeholder="14 chiffres (SIRET) ou 9 chiffres (SIREN)"
                    />
                    {errors.siret && (
                        <p className="mt-1 text-sm text-red-400">{errors.siret}</p>
                    )}
                </div>

                {/* N° TVA intracommunautaire (optionnel) */}
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        N° TVA intracommunautaire
                    </label>
                    <input
                        type="text"
                        name="vatNumber"
                        value={billingData.vatNumber || ''}
                        onChange={handleChange}
                        disabled={disabled}
                        className={inputClass('vatNumber')}
                        placeholder="FR12345678901"
                    />
                    {errors.vatNumber && (
                        <p className="mt-1 text-sm text-red-400">{errors.vatNumber}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                        À renseigner si vous êtes assujetti à la TVA.
                    </p>
                </div>
            </div>

            {/* Coordonnées bancaires (émetteur des factures) */}
            {showBankDetails && (
                <div className="mt-6 pt-6 border-t border-white/10">
                    <div className="flex items-center mb-4">
                        <Landmark className="w-5 h-5 text-blue-400 mr-2" />
                        <h3 className="text-lg font-semibold text-white">Coordonnées bancaires</h3>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">
                        Affichées sur vos factures pour le règlement par virement.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Bénéficiaire
                            </label>
                            <input
                                type="text"
                                name="billingBeneficiary"
                                value={billingData.billingBeneficiary || ''}
                                onChange={handleChange}
                                disabled={disabled}
                                className={inputClass('billingBeneficiary')}
                                placeholder="Nom du titulaire du compte"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                IBAN
                            </label>
                            <input
                                type="text"
                                name="billingIban"
                                value={billingData.billingIban || ''}
                                onChange={handleChange}
                                disabled={disabled}
                                className={inputClass('billingIban')}
                                placeholder="FR76 ..."
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                BIC
                            </label>
                            <input
                                type="text"
                                name="billingBic"
                                value={billingData.billingBic || ''}
                                onChange={handleChange}
                                disabled={disabled}
                                className={inputClass('billingBic')}
                                placeholder="ABCDEFGH"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Checkbox pour sauvegarder */}
            {showSaveCheckbox && (
                <div className="mt-4 pt-4 border-t border-white/10">
                    <label className="flex items-start cursor-pointer">
                        <input
                            type="checkbox"
                            checked={saveToProfile}
                            onChange={(e) => onSaveToProfileChange(e.target.checked)}
                            disabled={disabled}
                            className="mt-1 w-4 h-4 text-yellow-500 bg-black/30 border-white/15 rounded focus:ring-yellow-500 focus:ring-2"
                        />
                        <span className="ml-3">
                            <span className="text-sm text-gray-300">
                                {billingData.billingName ? 'Mettre à jour mon adresse par défaut' : 'Enregistrer comme adresse par défaut'}
                            </span>
                            <span className="block text-xs text-gray-500 mt-1">
                                Vous pouvez gérer votre adresse de facturation dans les paramètres de votre profil
                            </span>
                        </span>
                    </label>
                </div>
            )}
        </div>
    );
};

export default BillingAddressForm;
